import { Component, EventEmitter, Input, Output, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { AutoCompleteModule } from 'primeng/autocomplete'; // For prefix and phone type autocomplete

// Services
import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { AjaxService } from '../../../../../services/ajax.service';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';

import { COMP_PROFILES } from '../../../../../services/account-api.constants';

export interface Profiler {
	id: string;
	recId?: number;
	profile: string;
	description: string;
	isActive: number;
	isApproved: number;
}

// Interface for dropdown options
interface SelectItem {
	label: string;
	value: string;
	code?: string;
}

@Component({
	selector: 'app-profilers',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		InputTextModule,
		ButtonModule,
		CardModule,
		AutoCompleteModule,
		TagModule,
		ToastModule,
		TableModule,
		TooltipModule
	],
	templateUrl: './profilers.component.html',
	styleUrls: ['./profilers.component.scss'],
	providers: [MessageService]
})
export class ProfilersComponent implements OnInit {
	@Input() profiles: Profiler[] = [];
	@Output() profilesChange = new EventEmitter<Profiler[]>();
	@Output() nextStep = new EventEmitter<void>();
	@Output() prevStep = new EventEmitter<void>();
	@Output() saveAndClose = new EventEmitter<Profiler[]>();
	@Output() skipToLastStep = new EventEmitter<void>();

	@Input() customerRecId!: number;

	readonly DESCRIPTION_MAX_LENGTH = 250;

	isDialogMode = false;
	isCreate = false;
	newEntry: Profiler = { id: '', profile: '', description: '', isActive: 1, isApproved: 1 };

	profOptions: SelectItem[] = [];
	filteredProfOptions: SelectItem[] = [];

	constructor(
		private messageService: MessageService,
		public translateServ: TranslateService,
		private ajaxServ: AjaxService,
		public dirServ: DirectionService,
		private viewScopeService: ViewScopeService,
		@Optional() public ref: DynamicDialogRef,
		@Optional() public config: DynamicDialogConfig,
		@Optional() private maintainAccountService: MaintainAccountService
	) { }

	ngOnInit(): void {
		this.loadProfs();
		if (this.config && this.config.data) {
			this.isDialogMode = this.config.data.isDialog;
			this.customerRecId = this.config.data.customerRecId;
			this.isCreate = this.config.data.isCreate;

			if (!this.isCreate && this.config.data.profilerEntry) {
				this.profiles = [this.config.data.profilerEntry];
			} else {
				this.profiles = [];
			}
		} else {
			this.isDialogMode = false;
			this.isCreate = true;
			this.profiles.forEach(item => {
				if (!item.id) {
					item.id = this.generateUniqueId();
				}
			});
			this.resetNewEntry();
		}
	}

	private generateUniqueId(): string {
		return 'id_' + Math.random().toString(36).substring(2, 9);
	}

	resetNewEntry(): void {
		this.newEntry = {
			id: this.generateUniqueId(),
			profile: '',
			description: '',
			isActive: 1,
			isApproved: 1
		};
	}

	addEntry(): void {
		if (!this.newEntry.profile.trim()) {
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Profile name is required.',
				life: 3000
			});
			return;
		}
		this.profiles.push({ ...this.newEntry });
		this.emitChanges();
		if (this.isCreate) this.resetNewEntry();
	}

	removeEntry(entry: Profiler): void {
		this.profiles = this.profiles.filter(e => e.id !== entry.id);
		this.emitChanges();
	}

	emitChanges(): void {
		this.profilesChange.emit(this.profiles);
	}

	getRemainingChars(desc?: string): number {
		return this.DESCRIPTION_MAX_LENGTH - (desc?.length ?? 0);
	}

	toggleIsActive(entry: Profiler): void {
		entry.isActive = entry.isActive === 1 ? 0 : 1;
	}

	toggleIsApproved(entry: Profiler): void {
		entry.isApproved = entry.isApproved === 1 ? 0 : 1;
	}

	goToPreviousStep(): void {
		this.prevStep.emit();
	}

	validateAndProceed(): void {
		let isValid = true;
		for (const entry of this.profiles) {
			if (!entry.profile.trim()) {
				isValid = false;
				break;
			}
		}
		if (isValid) this.nextStep.emit();
	}

	applyRowChanges(entry: Profiler): void {
		this.emitChanges();
	}

	saveAndSubmit(): void {
		let isValid = true;
		for (const entry of this.profiles) {
			if (!entry.profile.trim()) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: `Name for entry "${entry.id}" is required.`,
					life: 3000
				});
				break;
			}
			if ((entry.description ?? '').length > this.DESCRIPTION_MAX_LENGTH) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: `Description for "${entry.profile}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
					life: 3000
				});
				break;
			}
		}

		if (!isValid) return;

		if (this.maintainAccountService) {
			const requests = this.profiles.map(entry => {
				const basePayload: any = {
					ProfileRecID: Number(entry.recId),
					CustomerSupplierRecID: this.customerRecId,
					Descr: entry.description || '',
					IsActive: entry.isActive,
					IsApproved: entry.isApproved,
					UserRecID: this.viewScopeService.getCurrentOpertaingUserId()
				};
				if (this.isCreate) {
					basePayload.CustomerSupplierRecID = this.customerRecId;
				} else {
					basePayload.Profile_CustomerSupplierRecID = entry.id;
				}
				return basePayload;
			});

			console.log("Data we will save:", requests);
			requests.forEach(record => {
				this.maintainAccountService.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1), JSON.stringify(record)).subscribe(
					{
						next: (response) => {
							this.messageService.add({
								severity: 'success',
								summary: 'Success',
								detail: `Saved record for "${record.profile}".`,
								life: 2000
							});
							if (this.ref && record === requests[requests.length - 1]) {
								this.ref.close(response);
							}
						},
						error: (error) => {
							this.messageService.add({
								severity: 'error',
								summary: 'Error',
								detail: `Failed to save record for "${record.profile}".`,
								life: 3000
							});
						},
						complete: () => {
							this.messageService.add({
								severity: 'success',
								summary: 'Complete',
								detail: 'All records processed.',
								life: 2000
							});
							if (this.ref) {
								this.ref.close(true);
							}
						}
					});
			});
		} else {
			this.messageService.add({
				severity: 'warn',
				summary: 'Warning',
				detail: 'This functionality is not available in standalone mode.',
				life: 3000
			});
		}
	}

	closeDialog(): void {
		if (this.ref) this.ref.close(false);
	}

	private loadProfs() {
		const url = this.ajaxServ.getBaseOfApi() + COMP_PROFILES + this.viewScopeService.getCurrentOpertaingUserId().toString();
		this.ajaxServ.getData(url).subscribe((response: any) => {
			const data = response as any[];
			this.profOptions = data.map(item => ({
				label: item.Profile,
				value: item.Profile,
				code: String(item.ProfileRecID)
			}));
		});
	}

	searchProfs(event: any, entry: Profiler): void {
		const q = (event.query || '').toLowerCase();
		this.filteredProfOptions = this.profOptions.filter(o =>
			o.label.toLowerCase().includes(q)
		);
	}

	onProfSelected(event: any, entry: Profiler): void {
		const selectedOption = event.value as SelectItem;
		if (selectedOption) {
			entry.recId = Number(selectedOption.code);
			entry.profile = selectedOption.label;
		}
		this.emitChanges();
	}
}
